import { Link } from "wouter";

export default function AboutSection() {
  return (
    <section id="about" className="py-20 md:py-28 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-[rgba(125,155,118,0.05)] -mr-16 -mt-16"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-[rgba(26,54,93,0.05)] -ml-16 -mb-16"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-[rgba(26,54,93,0.1)] text-[#1a365d] rounded-full text-sm font-medium mb-4">Meet Your Draper, Utah Accounting Team</span>
          <h2 className="section-title">About Sarah - Serving Salt Lake County & Beyond</h2>
          <div className="elegant-divider"></div>
        </div>
        
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="md:w-2/5 relative">
            {/* Decorative frame */}
            <div className="absolute inset-0 border-8 border-[rgba(125,155,118,0.1)] rounded-lg transform translate-x-4 translate-y-4"></div>
            
            <div className="relative z-10 rounded-lg overflow-hidden shadow-elegant">
              <picture>
                <source srcSet="/images/staff/webp/sarah-adam-tree.webp" type="image/webp" />
                <source srcSet="/images/staff/sarah-adam-tree.jpg" type="image/jpeg" />
                <img 
                  src="/images/staff/sarah-adam-tree.jpg" 
                  alt="Sarah & Adam Willardsen, SW Accounting - Draper, Utah's Premier Accounting Professionals" 
                  className="w-full h-auto transition-transform duration-700 hover:scale-105" 
                  loading="lazy" 
                  width="600" 
                  height="400"
                  title="Sarah & Adam Willardsen - Salt Lake County's Trusted Accountants Serving Utah and Nationwide Clients"
                />
              </picture>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[rgba(26,54,93,0.8)] to-transparent p-6 text-white">
                <h4 className="font-serif text-xl font-semibold">Sarah & Adam Willardsen</h4>
                <p className="text-sm text-white/80">Founders, SW Accounting</p>
              </div>
            </div>
          </div>
          
          <div className="md:w-3/5">
            <h3 className="font-serif text-2xl font-semibold text-[#1a365d] mb-3 accent-line pb-2">Professional Background</h3>
            <p className="mb-6 leading-relaxed text-gray-700">
              Based in Draper, Utah, with over two decades of accounting experience, I've helped businesses throughout Salt Lake County, Utah, and across the United States navigate their financial journeys with confidence and precision. My commitment to excellence has built a reputation of trust with local and nationwide clients through our secure, in-person and remote accounting services.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-[rgba(125,155,118,0.05)] p-5 rounded-lg border-l-4 border-[#7d9b76] shadow-elegant card-hover-effect">
                <div className="flex items-start">
                  <div className="bg-white rounded-full p-3 shadow-sm text-[#7d9b76] mr-4">
                    <i className="fas fa-graduation-cap text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-[#1a365d]">Education</h4>
                    <p className="text-gray-700">Bachelor of Science – Accounting</p>
                  </div>
                </div>
              </div>
              <div className="bg-[rgba(125,155,118,0.05)] p-5 rounded-lg border-l-4 border-[#7d9b76] shadow-elegant card-hover-effect">
                <div className="flex items-start">
                  <div className="bg-white rounded-full p-3 shadow-sm text-[#7d9b76] mr-4">
                    <i className="fas fa-award text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-[#1a365d]">Certification</h4>
                    <p className="text-gray-700">Certified QuickBooks Pro Advisor</p>
                  </div>
                </div>
              </div>
              <div className="bg-[rgba(125,155,118,0.05)] p-5 rounded-lg border-l-4 border-[#7d9b76] shadow-elegant card-hover-effect">
                <div className="flex items-start">
                  <div className="bg-white rounded-full p-3 shadow-sm text-[#7d9b76] mr-4">
                    <i className="fas fa-certificate text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-[#1a365d]">Credentials</h4>
                    <p className="text-gray-700">Sage-Intacct & Financial Officer</p>
                  </div>
                </div>
              </div>
              <div className="bg-[rgba(125,155,118,0.05)] p-5 rounded-lg border-l-4 border-[#7d9b76] shadow-elegant card-hover-effect">
                <div className="flex items-start">
                  <div className="bg-white rounded-full p-3 shadow-sm text-[#7d9b76] mr-4">
                    <i className="fas fa-clock text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-[#1a365d]">Experience</h4>
                    <p className="text-gray-700">20+ Years of Financial Expertise</p>
                  </div>
                </div>
              </div>
            </div>
            
            <blockquote className="italic text-gray-600 border-l-4 border-[#7d9b76] pl-4 mb-8">
              "From our Draper, Utah office, my approach combines technical expertise with a genuine understanding of your business needs, whether you're in Salt Lake City, elsewhere in Utah, or across the country. I believe in building lasting relationships based on trust, accuracy, and financial clarity through our secure in-person and virtual services available to local and nationwide clients."
            </blockquote>
            
            <div className="flex items-center gap-4">
              <Link 
                href="/booking"
                className="inline-flex items-center bg-[#1a365d] hover:bg-opacity-90 text-white font-bold py-3 px-8 rounded-md transition-all duration-300 shadow-elegant"
              >
                <i className="fas fa-calendar-alt mr-2"></i>
                Schedule a Meeting
              </Link>
              <Link 
                href="/about"
                className="inline-flex items-center text-[#1a365d] hover:text-[#7d9b76] font-medium transition-colors duration-300"
              >
                Learn More About Us
                <i className="fas fa-arrow-right ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
